import type { HTTP } from "@/core/http";
import type {
  AdminUser,
  UserHistory,
  UserListItem,
  UserRoleAssignment,
  UserSkillAssignment,
  UserCreateRequest,
  UserUpdateRequest,
  UsersMetadata,
} from "../types";

export class UserService {
  constructor(private readonly httpClient: HTTP) {}

  async getUsers(): Promise<UserListItem[]> {
    return this.httpClient.get<UserListItem[]>("/platform/users");
  }

  async getUserMetadata(): Promise<UsersMetadata> {
    return this.httpClient.get<UsersMetadata>("/platform/users/metadata");
  }

  async getUser(userId: number): Promise<AdminUser> {
    return this.httpClient.get<AdminUser>(`/platform/users/${userId}`);
  }

  /**
   * Tenant-admin signup: Platform creates Cognito user + tenant DB row and sends invite.
   */
  async createUser(data: UserCreateRequest): Promise<AdminUser> {
    return this.httpClient.post<AdminUser, UserCreateRequest>(
      "/platform/users",
      { body: data },
    );
  }

  /** Unified PUT — status, remarks, optional roles and skills. */
  async updateUser(userId: number, data: UserUpdateRequest): Promise<AdminUser> {
    return this.httpClient.put<AdminUser, UserUpdateRequest>(
      `/platform/users/${userId}`,
      { body: data },
    );
  }

  async getUserHistory(userId: number): Promise<UserHistory[]> {
    return this.httpClient.get<UserHistory[]>(
      `/platform/users/${userId}/history`,
    );
  }

  async getUserRoles(userId: number): Promise<UserRoleAssignment[]> {
    return this.httpClient.get<UserRoleAssignment[]>(
      `/platform/users/${userId}/roles`,
    );
  }

  async getUserSkills(userId: number): Promise<UserSkillAssignment[]> {
    return this.httpClient.get<UserSkillAssignment[]>(
      `/platform/users/${userId}/skills`,
    );
  }
}

export type { AdminUser, UserListItem };
